import { useState, useRef, useEffect } from 'react';
import { AppPage, AppCard } from '../../components/general';
import { Toast } from 'primereact/toast';
import { DataTable, TableDefinition, DataTableSearchCreateHeader } from '../../components/datatable';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ProgressBar } from 'primereact/progressbar';
import { BlockUI } from 'primereact/blockui';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Button } from 'primereact/button';
import { Sidebar } from 'primereact/sidebar';
import { Tag } from 'primereact/tag';
import { CustomFieldViewModel, CustomFieldType, CreateOrUpdateCustomFieldViewModel } from '../../models/configuration';
import * as configurationService from '../../services/configurationService';
import { EditCustomField } from './EditCustomField';

export const CustomFields = (): JSX.Element => {
  const toast = useRef<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [customFields, setCustomFields] = useState<CustomFieldViewModel[]>([]);
  const [search, setSearch] = useState<string>('');
  const [editorSidebarVisible, setEditorSidebarVisible] = useState<boolean>(false);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(window.innerWidth < 768);
  const [selectedField, setSelectedField] = useState<CustomFieldViewModel | null>(null);
  const [confirmDialogVisible, setConfirmDialogVisible] = useState<boolean>(false);
  const [confirmationData, setConfirmationData] = useState<CustomFieldViewModel | null>(null);
  const [confirmationOperation, setConfirmationOperation] = useState<'save' | 'delete'>('save');

  useEffect(() => {
    fetchCustomFields();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchCustomFields = (): void => {
    setIsLoading(true);
    configurationService
      .getAllCustomFields()
      .then((fields) => setCustomFields(fields))
      .catch((error) => showError(error.message))
      .finally(() => setIsLoading(false));
  };

  const showMessage = (
    severity: 'success' | 'info' | 'warn' | 'error',
    summary: string,
    detail: string,
    life: number = 3000
  ) => {
    toast.current?.show({ severity, summary, detail, life });
  };

  const showError = (message: string) => {
    showMessage('error', 'Error', message, 5000);
  };

  const onEdit = (field: CustomFieldViewModel): void => {
    setSelectedField(field);
    setEditorSidebarVisible(true);
  };

  const onDelete = (field: CustomFieldViewModel) => {
    setSelectedField(field);
    setConfirmationOperation('delete');
    setConfirmationData(field);
    setConfirmDialogVisible(true);
  };

  const [pendingSavePayload, setPendingSavePayload] = useState<CreateOrUpdateCustomFieldViewModel | null>(null);

  const handleSaveRequest = (payload: CreateOrUpdateCustomFieldViewModel) => {
    setPendingSavePayload(payload);
    setConfirmationOperation('save');
    setConfirmDialogVisible(true);
  };

  const handleConfirmAccept = () => {
    setConfirmDialogVisible(false);
    if (confirmationOperation === 'delete' && confirmationData) {
      deleteField(confirmationData);
    } else if (confirmationOperation === 'save' && pendingSavePayload) {
      if (selectedField) {
        updateField(selectedField.customFieldId, pendingSavePayload);
      } else {
        createField(pendingSavePayload);
      }
    }
  };

  const handleConfirmReject = () => {
    setConfirmDialogVisible(false);
  };

  const createField = (payload: CreateOrUpdateCustomFieldViewModel) => {
    setIsSubmitting(true);
    configurationService
      .createCustomField(payload)
      .then(() => {
        showMessage('success', 'Success', `Custom field "${payload.name}" successfully created`);
        setEditorSidebarVisible(false);
        fetchCustomFields();
      })
      .catch((error) => showError(error.message))
      .finally(() => setIsSubmitting(false));
  };

  const updateField = (id: number, payload: CreateOrUpdateCustomFieldViewModel) => {
    setIsSubmitting(true);
    configurationService
      .updateCustomField(id, payload)
      .then(() => {
        showMessage('success', 'Success', `Custom field "${payload.name}" successfully updated`);
        setEditorSidebarVisible(false);
        fetchCustomFields();
      })
      .catch((error) => showError(error.message))
      .finally(() => setIsSubmitting(false));
  };

  const deleteField = (field: CustomFieldViewModel) => {
    setIsDeleting(true);
    configurationService
      .deleteCustomField(field.customFieldId)
      .then(() => {
        showMessage('success', 'Success', `Custom field "${field.name}" successfully deleted`);
        fetchCustomFields();
      })
      .catch((error) => showError(error.message))
      .finally(() => setIsDeleting(false));
  };

  const fieldTypeTemplate = (field: CustomFieldViewModel) => {
    if (!field.fieldType || field.fieldType === CustomFieldType.None) {
      return <span className="text-color-secondary">—</span>;
    }
    return <span>{field.fieldType}</span>;
  };

  const statusTemplate = (field: CustomFieldViewModel) => {
    return field.inactive ? (
      <Tag value="Inactive" severity="warning" />
    ) : (
      <Tag value="Active" severity="success" />
    );
  };

  const optionsCountTemplate = (field: CustomFieldViewModel) => {
    const count = field.options?.length ?? 0;
    if (count === 0) return <span className="text-color-secondary">—</span>;
    return <span>{count} option{count !== 1 ? 's' : ''}</span>;
  };

  const actionTemplate = (field: CustomFieldViewModel) => {
    return (
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-success"
          tooltip="Edit"
          tooltipOptions={{ position: 'top' }}
          onClick={() => onEdit(field)}
        />
        <Button
          type="button"
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger"
          tooltip="Delete"
          tooltipOptions={{ position: 'top' }}
          onClick={() => onDelete(field)}
        />
      </div>
    );
  };

  const tableDef: TableDefinition<CustomFieldViewModel> = {
    columns: [
      {
        field: 'name',
        header: 'Name',
        dataType: 'text',
        sortable: true,
        filter: true,
      },
      {
        field: 'dataType',
        header: 'Data Type',
        dataType: 'text',
      },
      {
        header: 'Field Type',
        dataType: 'none',
        body: fieldTypeTemplate,
        sortable: false,
        filter: false,
      },
      {
        header: 'Options',
        dataType: 'none',
        body: optionsCountTemplate,
        sortable: false,
        filter: false,
      },
      {
        header: 'Status',
        dataType: 'none',
        body: statusTemplate,
        sortable: false,
        filter: false,
      },
      {
        header: 'Actions',
        body: actionTemplate,
        dataType: 'none',
        sortable: false,
        filter: false,
      },
    ],
  };

  const renderContent = () => {
    return (
      <>
        <Toast ref={toast} />
        <ConfirmDialog />

        <Dialog
          header={confirmationOperation === 'save' ? 'Confirm Changes' : 'Confirm Delete'}
          visible={confirmDialogVisible}
          onHide={handleConfirmReject}
          style={{ width: '450px' }}
          footer={
            <div className="flex justify-content-end gap-2">
              <Button label="No" icon="pi pi-times" onClick={handleConfirmReject} className="p-button-text" />
              <Button label="Yes" icon="pi pi-check" onClick={handleConfirmAccept} autoFocus />
            </div>
          }
        >
          <div className="flex align-items-center gap-3">
            <i className="pi pi-exclamation-triangle" style={{ fontSize: '2rem', color: 'var(--yellow-500)' }} />
            {confirmationOperation === 'save' ? (
              <span>Are you sure you want to save these changes?</span>
            ) : (
              <span>Are you sure you want to delete the custom field: {confirmationData?.name}?</span>
            )}
          </div>
        </Dialog>

        <Sidebar
          visible={editorSidebarVisible}
          position="right"
          onHide={() => setEditorSidebarVisible(false)}
          className="p-sidebar-md md:p-sidebar-lg"
          style={isSmallScreen ? undefined : { width: '100%', maxWidth: '700px' }}
          header={selectedField ? `Edit Custom Field - ${selectedField.name}` : 'Create Custom Field'}
          fullScreen={isSmallScreen}
          modal
        >
          <BlockUI blocked={isSubmitting} fullScreen={false} className="relative">
            {isSubmitting && (
              <div
                className="flex align-items-center justify-content-center"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  zIndex: 1,
                }}
              >
                <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
              </div>
            )}
            <EditCustomField
              key={selectedField?.customFieldId ?? 'new'}
              customField={selectedField}
              onSave={handleSaveRequest}
              onCancel={() => setEditorSidebarVisible(false)}
            />
          </BlockUI>
        </Sidebar>

        {isDeleting && (
          <div className="w-full mb-3">
            <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
            <div className="text-center mt-2 text-sm text-primary">Deleting custom field...</div>
          </div>
        )}

        <DataTable
          header={
            <DataTableSearchCreateHeader
              onCreate={() => {
                setSelectedField(null);
                setEditorSidebarVisible(true);
              }}
              onSearchChange={setSearch}
            />
          }
          tableDef={tableDef}
          data={customFields}
          globalFilterValue={search}
          loading={isLoading}
        />
      </>
    );
  };

  return (
    <AppPage title="Custom Fields" breadcrumbs="Admin - Custom Fields">
      <AppCard>{renderContent()}</AppCard>
    </AppPage>
  );
};
